import {
  IPlatformPrivateEvent,
  PlatformPrivateEvent,
  PlatformPrivateEventEmitter,
} from '@wix/public-editor-platform-events';
import { EventType } from '@wix/editor-platform-sdk-types';
import { PlatformPrivateServices } from '@wix/editor-platform-private-api';
import { ApplicationType } from '@wix/editor-application';

import { IApplicationSpec } from '../types';
import { ApplicationSpecExtractorComposite } from './getAppSpecsFromCSM';
import { AddonsExtractor } from './getAppSpecsFromCSM/AddonsExtractor';
import { PlatformWorkerManager } from './PlatformWorkerManager';

type AppRunParams = {
  firstInstall: boolean;
};

export class AppRunner {
  private events: PlatformPrivateEventEmitter;
  private applicationSpecExtractor = new ApplicationSpecExtractorComposite(
    this.ps,
  ).with(new AddonsExtractor());

  constructor(
    private readonly ps: PlatformPrivateServices,
    private manager: PlatformWorkerManager,
  ) {
    this.ps = ps;
    this.manager = manager;
    this.events = manager.events;
  }

  public async registerToolPanel(
    appSpec: IApplicationSpec,
    shouldOpenPanel?: boolean,
  ): Promise<void> {
    const { url, width, height, initialPosition } =
      appSpec.data?.toolPanelConfig || {};

    if (!url) {
      throw new Error('Tool panel URL is required');
    }

    // TODO: handle errors
    const openPanel = await this.ps.hostAPI.editor.registerToolsPanel(
      // @ts-expect-error
      { appDefinitionId: appSpec.appDefinitionId },
      {
        title: appSpec?.data?.marketData?.name || appSpec.appDefinitionName,
      },
      {
        url,
        // TODO: this can become "200px", "50%" etc in future. For now we simply assume numeric values like "250".
        width: Number(width),
        height: Number(height),
        initialPosition: {
          x: Number(initialPosition?.x),
          y: Number(initialPosition?.y),
        },
      },
    );

    if (shouldOpenPanel) {
      openPanel();
    }
  }

  public async runApplication(
    appSpec: IApplicationSpec,
    appRunParams: AppRunParams = { firstInstall: false },
  ) {
    try {
      await this.events.withEvent(
        this.events.factories.createRunApplicationEvent(appSpec),
        async () => {
          if (appSpec.type === ApplicationType.EditorAddon) {
            this.registerToolPanel(appSpec, appRunParams.firstInstall);
          }
          if (appSpec.url) {
            const workerAPI = await this.manager.getWorkerAPI();

            await workerAPI.runApplication(appSpec);
          }
        },
      );
    } catch (e: any) {
      console.error('runApplication', e);
      throw e;
    }
  }

  private async run(
    apps: IApplicationSpec[],
    appRunParams?: AppRunParams,
  ): Promise<PromiseSettledResult<void>[]> {
    return Promise.allSettled(
      apps.map((app) => this.runApplication.call(this, app, appRunParams)),
    );
  }

  private async runInstalledApp(event: IPlatformPrivateEvent) {
    if (
      event.type === PlatformPrivateEvent.HostEvent &&
      event.payload?.type === EventType.appInstalled
    ) {
      const apps = this.applicationSpecExtractor.execute().filter((app) => {
        return app.appDefinitionId === event.meta.appDefinitionId;
      });
      await this.run(apps, { firstInstall: true });
    }
  }

  private handleAppEvents() {
    this.events.subscribe(async (event: IPlatformPrivateEvent) => {
      await this.runInstalledApp(event);
    });
  }

  private async runAppsFromCSM() {
    const apps = this.applicationSpecExtractor.execute();

    if (apps.length === 0) {
      return;
    }

    this.manager.events.notify({
      type: PlatformPrivateEvent.ApplicationsSpecsReceived,
      payload: {
        appDefinitionIds: apps.map((app) => app.appDefinitionId),
      },
      meta: {},
    });

    await this.run(apps);
  }

  public async start() {
    await this.runAppsFromCSM();
    this.handleAppEvents();
  }
}
